import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { useDailyReportFormatValues } from "../hooks/useDailyReportDefaultValues";
import { useDailyReportMutation } from "../hooks/useDailyReportMutation";

import DailyReportFormForRoster from "./DailyReportFormForRoster";

import CustomHeader from "@/components/reusables/Headers.tsx";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { DailyReport, TDailyReport } from "@/schemas/daily-report.schema";

const AddReportFromRoster = (): React.JSX.Element => {
  const param = useParams();

  const form = useForm<TDailyReport>({
    resolver: zodResolver(DailyReport),

    mode: "onChange",
    defaultValues: {
      customerId: "",
      projectId: "",
      employeeWorkingHours: [],
      subcontractorWorkingHours: [],
      resourceRecoveryDetails: {
        usedTickets: [],
        manualData: [],
      },
      taskBreakdowns: [],
    },
  });

  const rosterId = param?.id;

  const { rosterEmployeeWH, daySheetIds, projectandCustomerInfo } =
    useDailyReportFormatValues({
      rosterId,
    });

  const { isMutating, mutateDailyReport, navigateBack } =
    useDailyReportMutation({
      form,
      daySheetIds,
    });

  const onSubmit = async (data: TDailyReport): Promise<void> => {
    await mutateDailyReport(data);
  };

  useEffect(() => {
    if (rosterEmployeeWH) {
      form.reset({
        ...form.getValues(),
        date: projectandCustomerInfo.date,
        customerId: projectandCustomerInfo.customerId,
        projectId: projectandCustomerInfo.projectId,
        employeeWorkingHours: rosterEmployeeWH,
      });
    }
  }, [rosterEmployeeWH, form.reset]);

  return (
    <section className='px-2'>
      <CustomHeader title={"Add New Report"} backBtn={navigateBack} />
      <Form {...form}>
        <form>
          <DailyReportFormForRoster />
          <div className='py-5 space-x-4'>
            <Button
              size={"lg"}
              type='button'
              variant={"ghost"}
              onClick={() => form.reset()}
              disabled={isMutating}
            >
              {Button_Names.Reset}
            </Button>
            <Button
              onClick={form.handleSubmit(onSubmit)}
              isLoading={isMutating}
            >
              Save Report
            </Button>
          </div>
        </form>
      </Form>
    </section>
  );
};

export default AddReportFromRoster;
